/**
 * Serializacao canonica + hash de instancia.
 *
 * O `instanceHash` e o recibo de reprodutibilidade: prova que a seed S do template T
 * na versao V produz exatamente este item. Ele so vale se a serializacao for canonica
 * -- chaves ordenadas, floats quantizados, funcoes ignoradas.
 */

import { quantize } from './num.ts';

/**
 * JSON com chaves ordenadas e floats quantizados.
 *
 * Funcoes sao omitidas de proposito: `AnimatedFigure.frame` e uma funcao e nao pode
 * entrar no hash (identidade de funcao nao e estavel). O que importa para a
 * reprodutibilidade sao os parametros que a produziram, nao o closure.
 */
export function canonicalJSON(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalize(value: unknown): unknown {
  if (value === null) return null;

  const t = typeof value;

  if (t === 'number') return quantize(value as number);
  if (t === 'string' || t === 'boolean') return value;
  if (t === 'undefined' || t === 'function' || t === 'symbol') return undefined;
  if (t === 'bigint') return (value as bigint).toString();

  if (Array.isArray(value)) return value.map(canonicalize);

  if (value instanceof Date) return value.toISOString();
  if (value instanceof Map) {
    return canonicalize(Object.fromEntries([...value.entries()].map(([k, v]) => [String(k), v])));
  }
  if (value instanceof Set) return [...value].map(canonicalize);

  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    const c = canonicalize(obj[key]);
    if (c !== undefined) out[key] = c;
  }
  return out;
}

/** FNV-1a de 64 bits, em hexadecimal. Curto, rapido e sem dependencia. */
export function fnv1a64(input: string): string {
  let hi = 0xcbf2_9ce4;
  let lo = 0x8422_2325;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    lo ^= c & 0xffff;
    hi ^= (c >>> 16) & 0xffff;
    // multiplica por 0x100000001b3 usando aritmetica de 32 bits em duas metades
    const lo0 = Math.imul(lo, 0x01b3) >>> 0;
    const hi0 = (Math.imul(hi, 0x01b3) + Math.imul(lo, 0x0000_0100)) >>> 0;
    lo = lo0;
    hi = hi0;
  }
  return (hi >>> 0).toString(16).padStart(8, '0') + (lo >>> 0).toString(16).padStart(8, '0');
}

/** Hash canonico de qualquer valor serializavel. */
export function hashOf(value: unknown): string {
  return fnv1a64(canonicalJSON(value));
}
